'use client';

/**
 * Diálogos da aplicação (SweetAlert2 com o tema do projeto).
 *
 * alert()/confirm()/prompt() nativos são desenhados pelo sistema: fonte fora
 * do tema, botão "OK" que não diz o que vai acontecer e nenhuma diferença
 * visual entre "salvo" e "isto apaga tudo". Aqui a ação destrutiva tem cor de
 * alerta e o botão diz o verbo.
 *
 * Helper único de propósito: o dia de trocar a paleta não pode ser um dia de
 * caçar Swal.fire pelo projeto.
 */
import Swal from 'sweetalert2';

const cor = (nome, queda) => {
  if (typeof window === 'undefined') return queda;
  return getComputedStyle(document.documentElement).getPropertyValue(nome).trim() || queda;
};

const CLARO = false;

const base = () => ({
  // Fundo OPACO: token translúcido deixaria a página aparecer atrás do texto.
  background: cor('--card', '#111827'),
  color: cor('--foreground', '#f3f4f6'),
  buttonsStyling: false,
  reverseButtons: true,
  focusCancel: true,
  customClass: {
    popup: 'rounded-xl',
    title: 'text-lg font-semibold',
    htmlContainer: 'text-sm opacity-80',
    actions: 'gap-2',
    confirmButton: 'rounded-lg px-4 h-10 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition-colors',
    cancelButton: CLARO
      ? 'rounded-lg px-4 h-10 text-sm font-medium border text-neutral-700 border-neutral-300 hover:bg-neutral-100 transition-colors'
      : 'rounded-lg px-4 h-10 text-sm font-medium border text-white/75 border-white/15 hover:bg-white/5 transition-colors',
  },
});

/** Confirmação. `perigo` pinta o botão de vermelho — usar no que apaga. */
export async function confirmar({ titulo, texto, confirmar: ok = 'Confirmar', cancelar = 'Cancelar', perigo = false }) {
  const cfg = base();
  const r = await Swal.fire({
    ...cfg,
    title: titulo,
    html: texto,
    icon: perigo ? 'warning' : 'question',
    showCancelButton: true,
    confirmButtonText: ok,
    cancelButtonText: cancelar,
    customClass: {
      ...cfg.customClass,
      confirmButton: perigo
        ? 'rounded-lg px-4 h-10 text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors'
        : cfg.customClass.confirmButton,
    },
  });
  return r.isConfirmed;
}

/** Aviso curto que não interrompe: sai sozinho no canto. */
export function aviso(titulo, tipo = 'success') {
  const cfg = base();
  return Swal.fire({
    ...cfg,
    toast: true,
    position: 'top-end',
    timer: tipo === 'error' ? 5000 : 3000,
    timerProgressBar: true,
    showConfirmButton: false,
    icon: tipo,
    title: titulo,
    customClass: { ...cfg.customClass, title: 'text-sm font-medium' },
  });
}

/** Erro que a pessoa precisa ler antes de seguir. */
export function erro(titulo, texto) {
  return Swal.fire({ ...base(), icon: 'error', title: titulo, html: texto, confirmButtonText: 'Entendi' });
}

/** Substitui o prompt(): devolve o texto digitado, ou null se cancelar. */
export async function pedirTexto({ titulo, texto, valor = '', placeholder = '', confirmar: ok = 'Confirmar', obrigatorio = false }) {
  const r = await Swal.fire({
    ...base(),
    title: titulo,
    html: texto,
    input: 'text',
    inputValue: valor,
    inputPlaceholder: placeholder,
    inputAttributes: { autocapitalize: 'off', autocorrect: 'off' },
    inputValidator: obrigatorio ? (v) => (v && v.trim() ? undefined : 'Preencha para continuar') : undefined,
    showCancelButton: true,
    confirmButtonText: ok,
    cancelButtonText: 'Cancelar',
  });
  return r.isConfirmed ? String(r.value ?? '') : null;
}
